import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  User,
  LogIn,
  LogOut,
  MessageSquare,
  Shield,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/badge';
import ConnectButtonAuth from '../auth/connect-button-auth';
import { UserMenu } from '../auth/user-menu';
import { useAuth } from '../../hooks/use-auth';
import {
  useAuthStore,
  selectUser,
  selectWalletAddress,
  selectIsAuthenticated,
  selectUserOid,
} from '../../hooks';
import { usePassportSSO } from '../../hooks/use-passport-sso';
import { NotificationBell } from '../notifications/notification-bell';
import { useSearch } from '../../hooks/use-search';
import { useCheckUserLogin } from '../../hooks/use-user';

import { createPortal } from 'react-dom';

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  // Use auth store for user data and authentication status
  const { user, logout } = useAuth();
  const walletAddress = useAuthStore(selectWalletAddress);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const userOid = useAuthStore(selectUserOid);
  const storeUser = useAuthStore((state) => state.user);
  const { search, setSearchQuery } = useSearch();

  // Passport SSO hook
  const { redirectToLogin } = usePassportSSO();

  // Use useCheckUserLogin to keep auth state in sync (runs in background)
  const { data: isLoggedIn, isLoading, isError } = useCheckUserLogin();

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getUserInitials = () => {
    if (!user || !user.name) return 'U';
    return user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  // Always transparent at top, blur on scroll
  const isTransparent = true;

  // Don't render anything if we're still loading authentication status
  if (isLoading) {
    return (
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-1000 backdrop-blur-lg ${
          scrolled || !isTransparent
            ? 'bg-black/05 border-b border-white/20'
            : 'bg-transparent border-b-0'
        }`}
      >
        <div className="container flex h-16 items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2"
              aria-label="Talktrail Home"
            >
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-2xl font-bold"
              >
                <span className="sr-only">Talktrail</span>
                <picture>
                  <source
                    srcSet="/ttlogo-64.webp 1x, /ttlogo-128.webp 2x"
                    type="image/webp"
                  />
                  <img
                    src="/ttlogo-64.png"
                    srcSet="/ttlogo-64.png 1x, /ttlogo-128.png 2x"
                    alt="Talktrail Logo"
                    width="64"
                    height="64"
                    className="hidden sm:block h-16 w-16 hover:opacity-80 transition-all duration-700 hover:scale-105 hover:translate-y-1 brightness-0 invert"
                    aria-hidden="true"
                  />
                </picture>
              </motion.span>
            </Link>
          </div>
          <div className="px-3 py-2">
            <ConnectButtonAuth />
          </div>
        </div>
      </header>
    );
  }

  if (isError) {
    return <div>Error loading authentication status.</div>;
  }

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-1000 backdrop-blur-lg ${
          scrolled || !isTransparent
            ? 'bg-black/05 border-b border-white/20'
            : 'bg-transparent border-b-0'
        }`}
      >
        <div className="container  flex h-16 items-center justify-between px-4 md:px-6">
          <div className="flex  items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
              aria-expanded={showMobileMenu}
            >
              {showMobileMenu ? (
                <X className="h-5 w-5 text-white" aria-hidden="true" />
              ) : (
                <Menu className="h-5 w-5 text-white" aria-hidden="true" />
              )}
            </Button>

            <Link
              to="/"
              className="flex items-center gap-2"
              aria-label="Talktrail Home"
            >
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-2xl font-bold"
              >
                <span className="sr-only">Talktrail</span>
                <picture>
                  <source
                    srcSet="/ttlogo-64.webp 1x, /ttlogo-128.webp 2x"
                    type="image/webp"
                  />
                  <img
                    src="/ttlogo-64.png"
                    srcSet="/ttlogo-64.png 1x, /ttlogo-128.png 2x"
                    alt="Talktrail Logo"
                    width="64"
                    height="64"
                    className="hidden sm:block h-16 w-16 hover:opacity-80 transition-all duration-700 hover:scale-105 hover:translate-y-1 brightness-0 invert"
                    aria-hidden="true"
                  />
                </picture>
              </motion.span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {/* Only show Forum, Profile, Notifications, Sign out if signed in */}
            {isAuthenticated ? (
              <>
                <Link to="/forum">
                  <Button
                    variant="ghost"
                    className="text-sm font-medium hover:bg-white/10 text-white hover:text-white"
                  >
                    Forum
                  </Button>
                </Link>

                <Link to="/gate-test">
                  <Button
                    variant="ghost"
                    className="text-sm font-medium hover:bg-white/10 text-white hover:text-white"
                  >
                    Gated Page
                  </Button>
                </Link>

                <Link to={`/profile/${storeUser?.passportId || storeUser?.oid || walletAddress}`}>
                  <Button
                    variant="ghost"
                    className="text-sm font-medium hover:bg-white/10 text-white hover:text-white"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Button>
                </Link>

                <NotificationBell />

                {/* User Menu - Shows wallet address and options */}
                <UserMenu />
              </>
            ) : (
              <>
                <Link
                  to="/about"
                  className="text-sm font-medium hover:text-white text-zinc-400 transition-colors"
                >
                  About
                </Link>
                <Link
                  to="/features"
                  className="text-sm font-medium hover:text-white text-zinc-400 transition-colors"
                >
                  Features
                </Link>
                <Button
                  onClick={redirectToLogin}
                  className="rounded-md bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Sign In
                </Button>
              </>
            )}
          </nav>

          <div className="px-3 py-2 flex items-center gap-2">
            {isAuthenticated ? (
              /* Show UserMenu on mobile */
              <div className="lg:hidden">
                <UserMenu />
              </div>
            ) : (
              <Button
                onClick={redirectToLogin}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 lg:hidden"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Navigation Portal */}
      {createPortal(
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 top-[64px] z-[100] bg-black/95 backdrop-blur-2xl lg:hidden flex flex-col border-t border-white/10"
            >
              {/* Background Gradients */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-violet-900/10 to-transparent opacity-50" />
                <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-cyan-500/10 blur-[100px] rounded-full" />
                <div className="absolute top-20 left-[-100px] w-[300px] h-[300px] bg-violet-500/10 blur-[100px] rounded-full" />
              </div>

              <nav className="flex flex-col p-6 relative z-10 h-full overflow-y-auto">
                <div className="flex flex-col gap-2 mt-4">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/forum"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <motion.div
                          whileHover={{
                            x: 10,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }}
                          className="flex items-center justify-between p-4 rounded-xl border border-white/5 bg-white/5 backdrop-blur-sm group transition-all"
                        >
                          <span className="text-lg font-semibold text-zinc-200 group-hover:text-white">
                            Forum
                          </span>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="bg-violet-500/10 text-violet-400 border-violet-500/20"
                            >
                              New
                            </Badge>
                            <MessageSquare className="h-5 w-5 text-zinc-500 group-hover:text-cyan-400 transition-colors" />
                          </div>
                        </motion.div>
                      </Link>

                      <Link
                        to="/gate-test"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <motion.div
                          whileHover={{
                            x: 10,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }}
                          className="flex items-center justify-between p-4 rounded-xl border border-white/5 bg-white/5 backdrop-blur-sm group transition-all"
                        >
                          <span className="text-lg font-semibold text-zinc-200 group-hover:text-white">
                            Gated Page
                          </span>
                          <Badge
                            variant="outline"
                            className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20"
                          >
                            Protected
                          </Badge>
                        </motion.div>
                      </Link>

                      <Link
                        to={`/profile/${storeUser?.passportId || storeUser?.oid || walletAddress}`}
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <motion.div
                          whileHover={{
                            x: 10,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }}
                          className="flex items-center justify-between p-4 rounded-xl border border-white/5 bg-white/5 backdrop-blur-sm group transition-all"
                        >
                          <span className="text-lg font-semibold text-zinc-200 group-hover:text-white">
                            Profile
                          </span>
                          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 p-[1px]">
                            <div className="h-full w-full rounded-full bg-black flex items-center justify-center">
                              <span className="text-xs font-bold text-white">
                                {getUserInitials()}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      </Link>

                      <Link
                        to="/notifications"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <motion.div
                          whileHover={{
                            x: 10,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                          }}
                          className="flex items-center justify-between p-4 rounded-xl border border-white/5 bg-white/5 backdrop-blur-sm group transition-all"
                        >
                          <span className="text-lg font-semibold text-zinc-200 group-hover:text-white">
                            Notifications
                          </span>
                          <NotificationBell />
                        </motion.div>
                      </Link>
                    </>
                  ) : (
                    <div className="flex flex-col gap-4 mt-8">
                      <Link
                        to="/about"
                        onClick={() => setShowMobileMenu(false)}
                        className="text-lg font-semibold text-zinc-400 hover:text-white transition-colors"
                      >
                        About
                      </Link>
                      <Link
                        to="/features"
                        onClick={() => setShowMobileMenu(false)}
                        className="text-lg font-semibold text-zinc-400 hover:text-white transition-colors"
                      >
                        Features
                      </Link>

                      <div className="text-center mb-4 mt-4">
                        <h3 className="text-xl font-bold text-white mb-2">
                          Welcome to Talktrail
                        </h3>
                        <p className="text-zinc-400 text-sm">
                          Join the conversation and connect with the community.
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setShowMobileMenu(false);
                          redirectToLogin();
                        }}
                        className="w-full h-12 text-lg bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-bold rounded-xl shadow-[0_0_20px_rgba(139,92,246,0.3)]"
                      >
                        <Shield className="mr-2 h-5 w-5" />
                        Sign In with Passport
                      </Button>
                      <Link
                        to="/sign-up"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <Button
                          variant="outline"
                          className="w-full h-12 text-lg border-white/10 text-white hover:bg-white/5 rounded-xl"
                        >
                          Learn More
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Footer Links */}
                <div className="mt-auto pt-8 pb-4 grid grid-cols-2 gap-4">
                  <Link
                    to="/about"
                    className="text-sm text-zinc-500 hover:text-white transition-colors"
                  >
                    About Us
                  </Link>
                  <Link
                    to="/privacy"
                    className="text-sm text-zinc-500 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/terms"
                    className="text-sm text-zinc-500 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                  <Link
                    to="/support"
                    className="text-sm text-zinc-500 hover:text-white transition-colors"
                  >
                    Support
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
